import React from "react";
import {
  FiMail,
  FiCheckCircle,
  FiEdit,
  FiPlay,
  FiArrowLeft,
} from "react-icons/fi";
import { FaRocket } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { scrollToTop } from "../../../utils/navigation";

const pageVariants = {
  initial: { opacity: 0, y: 50 },
  enter: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -50 },
};

export default function EmailResponse() {
  const navigate = useNavigate();

  const handleCall = () => {
    scrollToTop();
    navigate("/signup");
  };
  return (
    <AnimatePresence>
      <motion.div
        className="flex flex-col items-center min-h-screen font-sans text-gray-800 bg-gradient-to-br from-blue-50 via-indigo-100 to-blue-50"
        initial="initial"
        animate="enter"
        exit="exit"
        variants={pageVariants}
        transition={{ duration: 0.5 }}>
        <header className="w-full px-8 py-8 text-center text-white shadow-lg bg-gradient-to-r from-blue-400 to-indigo-600">
          <h1 className="text-4xl font-extrabold tracking-wide">
            Quick Email Response
          </h1>
          <p className="max-w-2xl mx-auto mt-3 text-lg font-light">
            B.E.T.H. can craft tailored messages for business proposals, tax
            inquiries, pitch decks, or any accounting-related communication you
            need.
          </p>
        </header>
        <main className="flex flex-col items-center w-full max-w-5xl px-6 mt-12 space-y-16">
          <section className="flex flex-col items-center space-y-4 text-center">
            <h2 className="flex items-center justify-center text-3xl font-semibold text-gray-700">
              Time-Saving, Improved Communication, Increased Productivity
            </h2>
            <p className="max-w-2xl mx-auto leading-relaxed text-gray-600">
              Whether it’s reaching out to clients, partners, or colleagues,
              B.E.T.H. ensures you get the right words effortlessly.
            </p>
            <motion.button
              className="flex items-center justify-center px-8 py-3 mt-6 font-semibold text-white transition duration-300 transform bg-indigo-600 rounded-full shadow-lg hover:bg-indigo-700 hover:scale-105"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => handleCall()}>
              Sign up for free <FaRocket className="ml-3" />
            </motion.button>
          </section>

          <section className="w-full px-4 space-y-12 text-center">
            <h3 className="text-2xl font-semibold text-gray-700">
              How It Works
            </h3>
            <div className="flex flex-col justify-center space-y-8 md:flex-row md:space-x-8 md:space-y-0">
              <motion.div
                className="flex-1 p-6 transition-transform transform bg-white rounded-lg shadow-lg hover:scale-105"
                whileHover={{
                  scale: 1.05,
                  boxShadow: "0px 10px 20px rgba(0, 0, 0, 0.2)",
                }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}>
                <h4 className="flex items-center justify-center space-x-2 text-lg font-semibold text-indigo-600">
                  <FiEdit className="mr-2" /> Input
                </h4>
                <p className="mt-2 text-gray-600">
                  Type your message or business communication details.
                </p>
              </motion.div>
              <motion.div
                className="flex-1 p-6 transition-transform transform bg-white rounded-lg shadow-lg hover:scale-105"
                whileHover={{
                  scale: 1.05,
                  boxShadow: "0px 10px 20px rgba(0, 0, 0, 0.2)",
                }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}>
                <h4 className="flex items-center justify-center space-x-2 text-lg font-semibold text-indigo-600">
                  <FiCheckCircle className="mr-2" /> Generate
                </h4>
                <p className="mt-2 text-gray-600">
                  B.E.T.H. crafts a tailored, professional message in seconds.
                </p>
              </motion.div>
              <motion.div
                className="flex-1 p-6 transition-transform transform bg-white rounded-lg shadow-lg hover:scale-105"
                whileHover={{
                  scale: 1.05,
                  boxShadow: "0px 10px 20px rgba(0, 0, 0, 0.2)",
                }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}>
                <h4 className="flex items-center justify-center space-x-2 text-lg font-semibold text-indigo-600">
                  <FiMail className="mr-2" /> Customize
                </h4>
                <p className="mt-2 text-gray-600">
                  Easily edit and personalize the response to suit your needs.
                </p>
              </motion.div>
            </div>
          </section>

          <section className="w-full space-y-4 text-center">
            <h3 className="flex items-center justify-center text-2xl font-semibold text-gray-700">
              Instructional Video <FiPlay className="ml-2 text-indigo-500" />
            </h3>
            <div className="relative flex items-center justify-center w-full h-full overflow-hidden bg-gray-100 rounded-lg shadow-lg">
              <video
                src="https://bethaibucket.s3.us-east-1.amazonaws.com/videos/QUICK%20EMAIL%20RESPONSE.mp4"
                controls
                className="object-fill w-full h-full rounded-lg"></video>
            </div>
          </section>
        </main>
        <aside className="fixed left-6 bottom-6">
          <Link
            to="/features"
            onClick={scrollToTop}
            className="flex items-center p-4 space-x-2 transition duration-300 bg-white rounded-lg shadow-lg  hover:bg-gray-100">
            <FiArrowLeft className="text-gray-600" />
            <span className="text-gray-600">Back to Features</span>
          </Link>
        </aside>

        <footer className="w-full py-8 text-center text-gray-600 bg-gradient-to-br from-blue-50 via-indigo-100 to-blue-50">
          <p className="text-sm">
            © {new Date().getFullYear()} Babylon by I-Lead. All rights reserved.
          </p>
        </footer>
      </motion.div>
    </AnimatePresence>
  );
}
